import { useFormik } from 'formik';
import React, { useEffect, useMemo } from 'react';
import { useMutation } from 'react-query';

import { Button, Dropdown, Form, InputField, Loader } from '@/components';
import { useTdaData } from '@/hooks/useTda';
import useToast from '@/hooks/useToast';
import {
  changeInitDateTda,
  changeNameTda,
  getAvailableNameTDA,
  postNameDateAdminTDA,
} from '@/services/tda';
import { DateGDT, SelectValue } from '@/types';
import { ResponseAdminTda } from '@/types/tda';
import {
  nameTdaSchema,
  oneSpaceBetweenWordMinTwoChar,
  TEXTS,
  validateFieldError,
} from '@/utils';

export const FormInfoTourney = React.memo(
  ({
    tourneyIdProps,
    tourneyDataProps,
    onSubmit,
  }: {
    tourneyDataProps: ResponseAdminTda;
    tourneyIdProps: number;
    onSubmit: () => void;
  }) => {
    const toast = useToast();
    const { tdaData, isSuccess } = useTdaData();

    const nextDates = useMemo(() => {
      return isSuccess
        ? tdaData?.fechasDisponibles.map((date: DateGDT) => ({
            label: date.nombre,
            value: date.orden,
          }))
        : [];
    }, [tdaData, isSuccess]);

    // post cambio nombre
    const { mutateAsync: postChangeName, isLoading: isLoadingNewName } =
      useMutation(
        async ({
          tourneyId,
          newName,
        }: {
          tourneyId: number;
          newName: string;
        }) => await changeNameTda(tourneyId, newName),
        {
          onSuccess: (data) => {
            if (data.estado) {
              toast.show('Cambio realizado exitosamente');
              setStatus(false);
              onSubmit();
            } else {
              toast.show(data.mensaje);
            }
          },
          onError: (err) => {
            const error = err as Error;
            toast.show(error.message);
          },
        }
      );

    // post cambio fecha inicio
    const { mutateAsync: postChangeDate, isLoading: isLoadingNewDate } =
      useMutation(
        async ({
          tourneyId,
          newDate,
        }: {
          tourneyId: number;
          newDate: number;
        }) => await changeInitDateTda(tourneyId, newDate),
        {
          onSuccess: (data) => {
            if (data.estado) {
              toast.show('Cambio realizado exitosamente');
              setStatus(false);
              onSubmit();
            } else {
              toast.show(data.mensaje);
            }
          },
          onError: (err) => {
            const error = err as Error;
            toast.show(error.message);
          },
        }
      );

    // post cambio nombre y fecha de inicio
    const {
      mutateAsync: postFullDataChange,
      isLoading: isLoadingFullDataChange,
    } = useMutation(
      async ({
        tourneyId,
        newDate,
        newName,
      }: {
        tourneyId: number;
        newDate: number;
        newName: string;
      }) => await postNameDateAdminTDA(tourneyId, newDate, newName),
      {
        onSuccess: (data) => {
          if (data.responseDate.estado && data.responseName.estado) {
            toast.show('Cambio realizado exitosamente');
            setStatus(false);
            onSubmit();
          } else {
            toast.show(data.responseDate.mensaje || data.responseName.mensaje);
          }
        },
        onError: (err) => {
          const error = err as Error;
          toast.show(error.message);
        },
      }
    );

    const dateSelected = useMemo(() => {
      return {
        value: tourneyDataProps.fechaCreacionOrden,
        label: tourneyDataProps.fechaCreacion,
      };
    }, [tourneyDataProps]);

    useEffect(() => {
      setFieldValue('tourneyName', tourneyDataProps.torneoAmigos);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tourneyDataProps.torneoAmigos]);

    const {
      touched,
      errors,
      isSubmitting,
      setFieldValue,
      setFieldError,
      handleSubmit,
      values,
      handleBlur,
      setFieldTouched,
      setStatus,
      status,
    } = useFormik({
      initialValues: {
        tourneyName: '',
        newInitData: dateSelected,
      },
      validateOnMount: false,
      validateOnBlur: true,
      validationSchema: nameTdaSchema,
      onSubmit: async (
        val: { tourneyName: string; newInitData: SelectValue },
        { setSubmitting }
      ) => {
        if (
          tourneyDataProps.permModFecha &&
          JSON.stringify(dateSelected) !== JSON.stringify(values.newInitData) &&
          tourneyDataProps.torneoAmigos === val.tourneyName
        ) {
          // console.log('cambio fecha inicio tda');
          postChangeDate({
            tourneyId: tourneyIdProps,
            newDate: val.newInitData.value,
          });
        } else if (
          tourneyDataProps.torneoAmigos !== val.tourneyName &&
          JSON.stringify(dateSelected) === JSON.stringify(values.newInitData)
        ) {
          // console.log('cambio nombre tda');
          postChangeName({
            tourneyId: tourneyIdProps,
            newName: val.tourneyName,
          });
        } else {
          // console.log('cambio nombre y fecha tda');
          postFullDataChange({
            tourneyId: tourneyIdProps,
            newDate: val.newInitData.value,
            newName: val.tourneyName,
          });
        }
        setSubmitting(false);
      },
    });

    const handleSelect = (e: SelectValue) => {
      setFieldValue('newInitData', e);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = oneSpaceBetweenWordMinTwoChar(e.target.value);

      setFieldValue('tourneyName', value);
      setFieldTouched('tourneyName', true, false);

      if (value.trim().length === 0) {
        setFieldError('tourneyName', TEXTS.minLetters);
        setStatus(false);
      } else if (value.trim() !== tourneyDataProps.torneoAmigos) {
        handleNameTournamentValidation(value);
      }
    };

    const MIN_CHARACTERS_TO_VALIDATE_NAME = 2;
    const handleNameTournamentValidation = async (tourneyName: string) => {
      if (tourneyName.trim().length >= MIN_CHARACTERS_TO_VALIDATE_NAME) {
        const response = await getAvailableNameTDA(
          encodeURIComponent(tourneyName)
        );

        if (!response.estado && 'codError' in response) {
          if (response.codError === 302) {
            setFieldError('tourneyName', TEXTS.nameExist);
          } else {
            setFieldError('tourneyName', response.mensaje);
          }
          setStatus(false);
        } else {
          setFieldError('tourneyName', undefined);
          setStatus(true);
        }
      } else {
        setStatus(false);
      }
    };

    const isSubmitable = useMemo(() => {
      return (
        (status &&
          !errors.tourneyName &&
          values.tourneyName !== tourneyDataProps.torneoAmigos &&
          values.tourneyName.trim().length >= MIN_CHARACTERS_TO_VALIDATE_NAME &&
          !isSubmitting) ||
        (tourneyDataProps.permModFecha &&
          values.newInitData.value !== tourneyDataProps.fechaCreacionOrden)
      );
    }, [
      status,
      errors.tourneyName,
      values.tourneyName,
      values.newInitData.value,
      tourneyDataProps.torneoAmigos,
      tourneyDataProps.permModFecha,
      tourneyDataProps.fechaCreacionOrden,
      isSubmitting,
    ]);

    const optionsAvailables = useMemo(() => {
      const existDateInitOnDates = nextDates?.find(
        (nextDates) => nextDates.value === dateSelected.value
      );

      if (existDateInitOnDates) {
        setFieldValue('newInitData', existDateInitOnDates);
        return nextDates;
      }

      return [dateSelected].concat(nextDates || []);
    }, [dateSelected, nextDates, setFieldValue]);

    return (
      <>
        <Loader
          isVisible={
            isLoadingNewName || isLoadingNewDate || isLoadingFullDataChange
          }
        />
        <Form.Form
          className="px-4 lg:px-0 mt-4"
          loading={isSubmitting}
          onSubmit={handleSubmit}
        >
          <Form.Field>
            <InputField
              behavior="default"
              error={validateFieldError(
                touched.tourneyName,
                errors.tourneyName,
                values.tourneyName.length
              )}
              label="Nombre del torneo"
              maxLength={25}
              name="tourneyName"
              placeholder="Nombre del torneo"
              success={
                !errors.tourneyName &&
                status &&
                values?.tourneyName !== tourneyDataProps.torneoAmigos
              }
              value={values.tourneyName}
              onBlur={(e) => {
                if (values.tourneyName.length >= 2) return;
                handleBlur(e);
              }}
              onChange={handleInputChange}
            />
          </Form.Field>
          <Form.Field className="mt-5">
            {!tourneyDataProps.permModFecha ? (
              <InputField
                readOnly
                autoComplete="off"
                behavior="default"
                label="Fecha de Inicio del Torneo"
                name="tourneyData.fechaCreacion"
                placeholder="Fecha de Inicio del Torneo"
                type="text"
                value={tourneyDataProps.fechaCreacion}
                onChange={() => {
                  // eslint-disable-next-line no-console
                  console.log('inhabilitado');
                }}
              />
            ) : (
              <Dropdown
                label="Fecha de Inicio de Torneo"
                options={optionsAvailables || []}
                selected={values.newInitData}
                onChange={handleSelect}
              />
            )}
          </Form.Field>
          <div className="lg:mt-9 mt-6">
            <Button
              className="w-full lg:max-w-[215px]"
              disabled={!isSubmitable}
              type="button"
              variant="primary"
              onClick={handleSubmit}
            >
              Guardar cambios
            </Button>
          </div>
        </Form.Form>
      </>
    );
  }
);

FormInfoTourney.displayName = 'FormInfoTourney';
